/**
 * The root component's helpers that both apps share, LIQUIDO and Polly.
 *
 * Each app's root component (src/liquido/root-app.vue, src/polly/polly-app.vue) mixes this in and renders the
 * global `<popup-modal id="rootPopupModal" ref="rootPopupModal">` itself. Everything here is then available to
 * every page as this.$root.<name> - showError(), scrollToTop(), isHomeScreenPWA, ...
 *
 * Usage:  mixins: [createAppShell({ pageOrder })]
 *
 * @param {Object} options
 * @param {Object} options.pageOrder route name -> number. A page slides in from the right when it is further
 *        to the right than the page before, and back from the left the other way round. Pages without a
 *        number - or with 0 - fade in.
 * @returns {Object} a Vue mixin
 */
import log from "loglevel"
import EventBus from "@/shared/services/event-bus.js"
import config from "config"

export function createAppShell({ pageOrder }) {
	return {
		data() {
			// These data attributes are reactive and available in EVERY sub-component as this.$root.<attributeName>
			return {
				transitionName: "", 	// CSS sliding transition between page components
				// Global popup-modal
				modalType: "success",
				modalTitle: "",
				modalMessage: "",
				modalContentClass: undefined,
				modalPrimaryButtonText: undefined,
				modalSecondaryButtonText: undefined,
			}
		},
		computed: {
			// Need to expose the module-level config import to the HTML template
			config() {
				return config
			},
			/**
			 * Is our PWA currently running as a standalone web application on the iOS or android home screen.
			 * The layout does not need it: it uses env(safe-area-inset-*) and the display-mode media query (liquido.css).
			 */
			isHomeScreenPWA() {
				const iosStandalone = window.navigator.standalone === true  // Apple iOs specific
				const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches  // generic standard
				return iosStandalone || displayModeStandalone
			},
			/**
			 * config.showDebugLog is a per-developer convenience (on-screen console.log, for debugging on
			 * a real phone with no attached devtools) - it must never show up in an automated test run
			 * regardless of what a developer's own local config.development.js has it set to, since the
			 * floating icon it renders can sit on top of real UI elements and block Cypress clicks
			 * (window.Cypress is only set inside an actual Cypress run, on every mode: local, mock or
			 * deployed).
			 */
			showMobileDebugLog() {
				return config.showDebugLog && !window.Cypress
			},
		},
		// watch the `$route` to determine the transition to use
		// https://router.vuejs.org/guide/advanced/transitions.html#per-route-transition
		watch: {
			$route(to, from) {
				this.transitionName = "fade"  // default transition: fade between pages
				const fromOrder = pageOrder[from.name]
				const toOrder = pageOrder[to.name]
				if (fromOrder && toOrder) {
					if (fromOrder < toOrder) { this.transitionName = "slide-left" }   // this is a prefix for the CSS classes in liquido.css
					if (fromOrder > toOrder) { this.transitionName = "slide-right"}
				}
			},
		},
		mounted() {
			// You can redirect all log messages to the mobile debug log by uncommenting the following line:
			// This has some consequences ... be carefull ... you for example loose context. And this conflicts with "loglevel" lib!
			//this.$refs["mobileDebugLogRef"]?.redefineConsoleMethods()
			this.$refs["mobileDebugLogRef"]?.info(config.LIQUIDO_API_URL)
			this.$refs["mobileDebugLogRef"]?.debug(config)
			log.debug("Full LIQUIDO config:\n" + JSON.stringify(config, null, 2))

			const safeAreaTop = getComputedStyle(document.documentElement)
				.getPropertyValue('--safe-area-inset-top')
			console.log('safe-area-inset-top:', safeAreaTop)
			this.$refs["mobileDebugLogRef"]?.debug("safe-area-inset-top: "+safeAreaTop)

			const safeAreaBottom = getComputedStyle(document.documentElement)
				.getPropertyValue('--safe-area-inset-bottom')
			console.log('safe-area-inset-bottom:', safeAreaBottom)
			this.$refs["mobileDebugLogRef"]?.debug("safe-area-inset-bottom: "+safeAreaBottom)

			if (this.isHomeScreenPWA) {
				log.debug("I am a full screen web app");
				this.$refs["mobileDebugLogRef"]?.debug("I am a full screen web app")
			}
		},
		methods: {
			/**
			 * We have one beautiful error/success modal
			 * that we reuse everywhere. This method is just a convenience shortcut
			 * available as this.$root.showError(errMsg, title) in all child components
			 */
			showError(errMsg, errTitle, primaryButtonText = this.$t('Ok'), secondaryButtonText = undefined, primaryCallback = undefined, secondaryCallback = undefined, errorCode = undefined) {
				return this.$refs.rootPopupModal.showError(errMsg, errTitle, primaryButtonText, secondaryButtonText, primaryCallback, secondaryCallback, errorCode)
			},
			showWarning(msg, title, primaryButtonText = this.$t('Ok'), secondaryButtonText = undefined, primaryCallback = undefined, secondaryCallback = undefined) {
				return this.$refs.rootPopupModal.showWarning(msg, title, primaryButtonText, secondaryButtonText, primaryCallback, secondaryCallback)
			},
			showSuccess(msg, title, primaryButtonText = this.$t('Ok'), secondaryButtonText = undefined, primaryCallback = undefined, secondaryCallback = undefined) {
				return this.$refs.rootPopupModal.showSuccess(msg, title, primaryButtonText, secondaryButtonText, primaryCallback, secondaryCallback)
			},
			showInfo(msg, title, primaryButtonText = this.$t('Ok'), secondaryButtonText = undefined, primaryCallback = undefined, secondaryCallback = undefined) {
				return this.$refs.rootPopupModal.showInfo(msg, title, primaryButtonText, secondaryButtonText, primaryCallback, secondaryCallback)
			},
			rootPopupClickPrimary(id) {
				EventBus.emit(EventBus.Event.ROOT_POPUP_CLICK_PRIMARY, id)
			},
			rootPopupClickSecondary(id) {
				EventBus.emit(EventBus.Event.ROOT_POPUP_CLICK_SECONDARY, id)
			},

			/**
			 * INTERNAL: One step in an animation
			 * adapted https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame
			 */
			step(timestamp, obj, attr, startTime, startValue, finalValue, durationMs) {
				if (startTime === undefined) startTime = timestamp
				const elapsed = timestamp - startTime
				obj[attr] = startValue + (finalValue - startValue) * (elapsed / durationMs)
				if (elapsed < durationMs) {
					window.requestAnimationFrame(timestamp => {
						this.step(timestamp, obj, attr, startTime, startValue, finalValue, durationMs)
					})
				} else {
					obj[attr] = finalValue  // make sure we have exactly the final value when animation finishes after durationMs
				}
			},

			/**
			 * Animate obj[attr] vom its current startVlaue to a finalValue in durationMs milliseconds.
			 * The animation will be performed with the window.requestAnimationFrame() method.
			 *
			 * @param obj a javascript object
			 * @param attr name of attribute in obj that shall be animated
			 * @param finalValue the final value that obj[attr] shall be animated to
			 * @param durationMs how long the duration shall take in milliseconds
			 *
			 */
			animate(obj, attr, finalValue, durationMs) {
				let startTime
				let startValue = obj[attr]
				window.requestAnimationFrame(timestamp => {
					this.step(timestamp, obj, attr, startTime, startValue, finalValue, durationMs)
				})
			},

			/*
			 * The scroll helpers scroll this.$el: the root component's own element, #rootApp, is the screen and the scroll
			 * container in both apps (see the screen layout in liquido.css). The document and #app never scroll.
			 */

			/** Scroll to top of page. No animation. */
			//TODO: Do this with  https://router.vuejs.org/guide/advanced/scroll-behavior.html#Delaying-the-scroll     "scrollBehaviour" in router!!!
			scrollToTop() {
				this.$nextTick(() => {
					this.$el.scrollTop = 0
				})
			},

			/** Animate scrolling to the very bottom of the page. */
			scrollToBottom(durationMs = 1000) {
				this.$nextTick(() => {
					let appContentHeight = document.getElementById("appContent").offsetHeight
					this.animate(this.$el, "scrollTop", appContentHeight, durationMs)
				})
			},

			/**
			 * scroll an HTML elemant right under the header
			 * (as far up as possible, depending on content below the elem)
			 * @param {Object} elem the dom elem
			 * @param {Number} marginTop margin to the top of the page (default = 50 px)
			 * @param {Number} durationMs duration of scroll animation (default = 500 ms)
			 */
			scrollElemToTop(elem, marginTop = 55, durationMs = 500) {
				//This would exist, but not in Safari for iOS :-(  elem.scrollIntoView({ behavior: 'smooth' });
				if (!elem) return
				let scrollTopFinalValue = elem.offsetTop - marginTop
				console.log("scrollElemToTop id=", elem.id,  elem.offsetTop, "-", marginTop, "=", scrollTopFinalValue)
				this.animate(this.$el, "scrollTop", scrollTopFinalValue, durationMs)
			},

			/** Check if the bottom of elem is scrolled into view */
			isBottomInView(elem) {
				let docViewTop = window.scrollTop
				let docViewBottom = docViewTop + document.body.clientHeight
				let elemTop = elem.offsetTop
				let elemBottom = elemTop + elem.height
				return elemBottom <= docViewBottom
			},
		},
	}
}
